import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { TrendingDown } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

const { Text } = Typography;

export interface BurndownPoint {
  date: string;
  ideal: number;
  remaining: number;
}

export interface BurndownWidgetProps {
  data?: readonly BurndownPoint[];
  className?: string;
  testId?: string;
  loading?: boolean;
}

export function BurndownWidget({ data = [], className, testId, loading = false }: BurndownWidgetProps): ReactElement {
  const { t } = useTranslation();
  
  const widgetContainerProps = {
    title: t('crm.dashboard.burndown.title'),
    icon: TrendingDown,
    iconColor: '#d97706', // warning amber 
    minHeight: 220,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse flex items-center justify-center rounded h-[170px] bg-stone-200/40" />
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center p-6 opacity-50 h-[170px]">
          <Text type="secondary" style={{ fontSize: 12 }}>{t('crm.dashboard.burndown.empty')}</Text>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={170}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="burnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#d97706" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" opacity={0.6} />
            <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#78716c' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#78716c' }} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ backgroundColor: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, fontSize: 11 }} />
            <Area type="monotone" dataKey="ideal" stroke="#a8a29e" strokeWidth={1} strokeDasharray="4 4" fill="none" name={t('crm.dashboard.burndown.ideal')} />
            <Area type="monotone" dataKey="remaining" stroke="#d97706" strokeWidth={2} fill="url(#burnGrad)" name={t('crm.dashboard.burndown.remaining')} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </WidgetContainer>
  );
}
